import { useCallback } from 'react';

import type {
    GetPayloadArg,
    HookOptions,
    QueryByIdPayloadCreatorType,
    UseEntity,
    UseEntityState,
} from './createEntityTypes';
import type { UsePaginatedEntity } from '../paginationEntity/createPaginationEntityTypes';
import { useDynamicEnabled } from '../utils/useDynamicEnabled';
import type { AgnosticCondition } from '../utils/internalTypings';

export const buildUseEntity = <
    QueryByIdPayloadCreator extends QueryByIdPayloadCreatorType<Entity>,
    Entity,
>(
    useEntity: UsePaginatedEntity<Entity, any>,
    invalidateById: (id: string) => void,
    condition?: AgnosticCondition<GetPayloadArg<QueryByIdPayloadCreator>>,
): UseEntity<Entity, GetPayloadArg<QueryByIdPayloadCreator>> => {
    return (
        args?: GetPayloadArg<QueryByIdPayloadCreator>,
        opts?: HookOptions,
    ): UseEntityState<Entity, GetPayloadArg<QueryByIdPayloadCreator>> => {
        const enabled = useDynamicEnabled({
            args,
            condition,
            fetchOnMount: opts?.fetchOnMount,
        });

        const { isPending, error, refetch, data } = useEntity(
            ((args as any)?.id ?? args) as string,
            {
                params: typeof args === 'object' && (args as any),
                enabled,
            },
        );
        const refetchCallback = useCallback(() => refetch() as Promise<Entity>, [refetch]);
        const invalidateByIdCallback = useCallback(
            (arg?: GetPayloadArg<QueryByIdPayloadCreator>) =>
                invalidateById(((arg as any)?.id ?? arg) as string),
            [invalidateById],
        );
        return {
            entity: data,
            error,
            refetch: refetchCallback,
            didInvalidate: false,
            isPending,
            invalidateById: invalidateByIdCallback,
        };
    };
};
